import { Box, CircularProgress, Typography } from "@mui/material"
import { SearchOutlined } from "@mui-symbols-material/w300"
import { useState } from "react"
import type { FC, UIEvent } from "react"
import { useFormContext } from "react-hook-form"

import { useGetRoles } from "../hooks"

import { FilterCheckbox } from "@/components/common/Filters"
import { InputField } from "@/components/common/InputField"
import type { UserQueryableFields } from "@/graphql/codegen/graphql"
import useDebounce from "@/utils/useDebounce"

interface RolesFilterProps {
  onFilterChange: (name: keyof UserQueryableFields, value: any) => void
}

export const RolesFilter: FC<RolesFilterProps> = ({ onFilterChange }) => {
  const [searchTerm, setSearchTerm] = useState("")
  const { control, watch } = useFormContext()
  const rolesValue = watch("roleIds") ?? []
  const debouncedSearchString = useDebounce(searchTerm, 300)

  const { roles, fetching, loadMore } = useGetRoles({ query: debouncedSearchString })

  const handleFilterChange = (newValue: string[]) => {
    onFilterChange("roleIds", newValue)
  }

  const canLoadMore = (element: UIEvent) => {
    const el = element.currentTarget
    return !fetching && el.scrollTop + el.clientHeight >= el.scrollHeight
  }

  return (
    <Box display='flex' flexDirection='column' height='100%'>
      <Typography variant='subtitle1' fontWeight={700} className='px-4 py-1.5 text-gray-700'>
        Roles
      </Typography>

      <Box className='flex-none p-4'>
        <InputField
          placeholder='Search by Role Name'
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          startAdornment={<SearchOutlined className='text-gray-600' />}
          endAdornment={fetching && <CircularProgress size={20} />}
        />
      </Box>

      <Box className='flex grow flex-col overflow-hidden'>
        <Box
          className='grow overflow-y-auto overflow-x-hidden px-8 py-2'
          onScroll={(event) => {
            if (canLoadMore(event)) {
              loadMore()
            }
          }}
        >
          {fetching && (
            <Box display='flex' justifyContent='center' py={2}>
              <CircularProgress size={24} />
            </Box>
          )}

          {!fetching && roles.length === 0 && (
            <Box display='flex' justifyContent='center' py={2}>
              <Typography variant='body2' color='textSecondary'>
                No roles found
              </Typography>
            </Box>
          )}

          {roles.map((role) => (
            <FilterCheckbox
              key={role.roleId}
              label={role.name}
              value={role.roleId}
              control={control}
              name='roleIds'
              values={rolesValue}
              onFilterChange={handleFilterChange}
            />
          ))}
        </Box>
      </Box>
    </Box>
  )
}
